import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { DhruvantaAuth } from '@dhruvanta/ui/react'
import { useAuth } from '../../context/AuthContext'
import {
  DHRUVANTA_BFF_BASE_PATH,
  DHRUVANTA_CARD_COPY,
  isDhruvantaBffEnabled,
  isDhruvantaLegacyEnabled,
  portalFromDom,
  variantForPortal,
} from '../../config/dhruvantaAuth'
import { LegacyDhruvantaSignInForm } from './LegacyDhruvantaSignInForm'
import type { PortalKey } from '../../types'
import './DhruvantaSignIn.css'

export interface DhruvantaSignInButtonProps {
  /**
   * Called after a successful Dhruvanta sign-in (e.g. to close a modal). Navigation
   * is otherwise driven by the host's `isAuthenticated` guard once the retail cookie
   * session is seated.
   */
  onSignedIn?: () => void
  /**
   * The role surface this sign-in seats. Defaults to the portal this bundle was
   * built for (`document.body.dataset.portal`), i.e. the correct one on every portal
   * without any host having to pass it.
   */
  portal?: PortalKey
}

/**
 * "Sign in with Dhruvanta" — the PRIMARY auth path for Dhruvanta Retail
 * (KAN-2036, epic KAN-2030).
 *
 * WHAT THIS IS: a trigger button plus the SHARED `<DhruvantaAuth>` card
 * (`@dhruvanta/ui/react`) running in `posture: 'bff'`. The card drives the whole
 * ceremony — email -> password / one-time code -> MFA (TOTP or passkey) — against
 * the same-origin `/idp/bff/*` broker. NO OIDC protocol code lives in this repo any
 * more: no discovery, no PKCE, no token exchange, no `redirect_uri` handling.
 *
 * TOKEN-FREE BROWSER (the platform's security invariant): the broker seals the IdP
 * access / refresh / id tokens SERVER-SIDE in an httpOnly AES-256-GCM cookie and
 * calls the retail bridge server-to-server, so the browser ends up holding only the
 * retail session cookie it has always held. `onAuthenticated` carries CLAIMS ONLY.
 * Nothing is written to web storage — enforced by `tests/tokenFreeInvariant.test.ts`.
 *
 * FIVE ROLE SURFACES: the portal is forwarded to the broker as an opaque seat
 * `variant`, which selects the retail bridge — and therefore the session cookie —
 * for that role. See `api/idp/brokerConfig.ts`.
 *
 * ADDITIVE + REVERSIBLE: the retail-native email/password + OTP form is untouched
 * and stays fully usable, so a misconfigured or unreachable IdP can never lock
 * anyone out. `VITE_DHRUVANTA_AUTH_MODE=legacy` restores the pre-KAN-2036
 * browser-side flow; `=off` hides this surface entirely.
 *
 * RENDERED IN A PORTAL: both hosts (`AuthModal`, `PortalLoginPage`) render this
 * INSIDE their own `<form>`, and the shared card renders a `<form>` of its own.
 * Nesting those is invalid HTML, so the card is portalled to `document.body`, and
 * the wrapper stops React's synthetic `submit` / `click` from bubbling back up the
 * React tree into the host form.
 */
export function DhruvantaSignInButton({ onSignedIn, portal }: DhruvantaSignInButtonProps = {}) {
  const { signInWithDhruvantaBff } = useAuth()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activePortal: PortalKey = portal ?? portalFromDom() ?? 'customer'
  const variant = variantForPortal(activePortal)
  const copy = DHRUVANTA_CARD_COPY[activePortal] ?? DHRUVANTA_CARD_COPY.customer

  const config = useMemo(
    () => ({
      posture: 'bff' as const,
      bffBasePath: DHRUVANTA_BFF_BASE_PATH,
      variant,
      features: {
        // The one-time code sits beside the password form (the IdP accepts either
        // as a verified first factor).
        otpLogin: 'optional' as const,
        // Passkey step-up whenever the IdP advertises one for the pending challenge.
        passkey: true,
        // Account creation stays on the IdP's own surfaces — retail must not grow a
        // second signup funnel for Dhruvanta identities.
        allowSignUp: false,
        allowForgot: true,
        rememberMe: true,
      },
    }),
    [variant],
  )

  const handleAuthenticated = useCallback(() => {
    setError(null)
    setOpen(false)
    // The retail session cookie is ALREADY seated (server-side, by the broker's
    // bridge call). All the SPA does is re-hydrate its state from it.
    void signInWithDhruvantaBff()
      .then(() => onSignedIn?.())
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Sign-in failed')
        setOpen(true)
      })
  }, [onSignedIn, signInWithDhruvantaBff])

  const handleError = useCallback((err: Error) => {
    // The card renders its own inline message for recoverable failures; this only
    // mirrors unrecoverable ones next to the trigger. It NEVER signs anyone out — a
    // 403 here means "authenticated but not entitled to this portal", which must
    // surface inline and leave any existing session intact.
    setError(err.message)
  }, [])

  // TODO(deprecation): the legacy branch drives the pre-KAN-2036 browser-side OIDC
  // client, which puts live IdP tokens in the JS heap. Retained ONLY as a
  // no-code-change rollback; do not build new work on it.
  if (isDhruvantaLegacyEnabled()) {
    return <LegacyDhruvantaSignInForm onSignedIn={onSignedIn} />
  }

  if (!isDhruvantaBffEnabled()) return null

  return (
    <div className="dhruvanta-sso">
      <div className="dhruvanta-sso__divider">
        <span>or</span>
      </div>
      <button
        type="button"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
        className="dhruvanta-sso__button"
        data-testid="dhruvanta-signin-trigger"
      >
        Sign in with Dhruvanta
      </button>
      {error && (
        <p className="dhruvanta-sso__error" role="alert">
          {error}
        </p>
      )}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="dhruvanta-sso__overlay"
            data-testid="dhruvanta-signin-panel"
            // See the docblock: the card lives in a portal, so its synthetic submit
            // / click must not bubble into the host's login <form>.
            onSubmit={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="dhruvanta-sso__overlay-surface" role="dialog" aria-modal="true">
              <button
                type="button"
                className="dhruvanta-sso__overlay-close"
                aria-label="Close Dhruvanta sign-in"
                onClick={() => setOpen(false)}
              >
                &times;
              </button>
              <DhruvantaAuth
                config={config}
                page={false}
                brand={{
                  eyebrow: copy.eyebrow,
                  title: 'Sign in with Dhruvanta',
                  subtitle: copy.subtitle,
                }}
                onAuthenticated={handleAuthenticated}
                onError={handleError}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

export default DhruvantaSignInButton
